import styled from 'styled-components';
import { OrderListRow } from './OrderListRow';
import { OrdersSkeletonRows } from './OrdersListSkeleton';
import { listBand, listSheet } from './listSheet';
import { OrderRow } from '../utils/orderRowFields';

/**
 * The phone rendering of the orders list: one row per order, no column headers. The rows carry no
 * chrome of their own — one white sheet holds them all and a hairline divider between each pair bands
 * them, which is a great deal quieter than a stack of outlined cards.
 *
 * Deliberately not height-constrained: the list flows in the document and uses the native page
 * scroll, instead of trapping a scroll region inside another one.
 */
export const OrdersMobileList: React.FC<{
  orders: OrderRow[];
  customerOrdersView?: boolean;
  /** Why the list is empty — no orders at all, or none matching the filters. Worded by the caller,
   *  which is the same expression the grid's own empty text reads. */
  emptyMessage: string;
  /** First load, with nothing to show yet. Placeholder rows stand in for the real ones. */
  isLoading?: boolean;
  /** Opens the tapped order in the details sheet. */
  onOrderSelect: (id: string) => void;
}> = ({ orders, customerOrdersView, emptyMessage, isLoading = false, onOrderSelect }) => {
  // Ahead of the empty check on purpose: an empty list that is still loading is not yet "no orders".
  if (isLoading) {
    return <OrdersSkeletonRows />;
  }

  if (orders.length === 0) {
    return <EmptyText>{emptyMessage}</EmptyText>;
  }

  return (
    <List>
      {orders.map((order) => (
        <Item key={order.id}>
          <OrderListRow order={order} customerOrdersView={customerOrdersView} onOrderSelect={onOrderSelect} />
        </Item>
      ))}
    </List>
  );
};

/**
 * A real list, so a screen reader announces how many orders there are and where in them it is — and
 * the sheet the rows sit on, which is what separates them from the shell's grey page.
 */
const List = styled.ul`
  ${listSheet}
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  ${listBand}
`;

// On the same sheet as the list it stands in for, so an empty result sits where the orders would
// have rather than shrinking back inside the gutter on its own.
const EmptyText = styled.p`
  ${listSheet}
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(2)};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.palette.text.secondary};
`;
